//Global Imports
import clsx from "clsx";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
//End Global Imports

//Local Imports
import TextField from "../../../components/ui/TextField";
import Button from "../../../components/ui/Button";
import { getErrorMessage, regexForPassword } from "../../../utils";
import Tooltip from "../../../components/ui/Tooltip";
import { DoubleVerifiedIcon } from "../../../assets/Icons";
//End Local Imports

const StepThree = ({ saveNewPassword }: any) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const labelStyle = "pb-3 text-sm md:text-base font-semibold text-black font-ssp px-8 md:px-0";

  type FormInputs = {
    password: string;
    confirmPassword: string;
  };

  const {
    formState: { errors },
    handleSubmit,
    getValues,
    control,
  } = useForm<FormInputs>();

  const handlePasswordBlur = () => {
    setShowTooltip(false);
  };

  const handlePasswordChange = (value: string) => {
    if (value?.length > 0) {
      const patternMatch = regexForPassword.test(value);
      if (!patternMatch) {
        setShowTooltip(true);
      } else {
        setShowTooltip(false);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit(saveNewPassword)}
      className="flex flex-col items-start mx-7 sm:mx-[52px]"
    >
      <div className={clsx(labelStyle, "md:pb-5 pb-3")}>Password</div>
      <div className="w-screen sm:w-[400px] px-6 sm:px-0 pb-7">
        <div className="w-full relative">
          <Controller
            control={control}
            name="password"
            rules={{ required: true, pattern: regexForPassword }}
            render={({ field: { name, value, onChange } }) => {
              return (
                <TextField
                  name={name}
                  type="password"
                  // placeholder="New password"
                  inputClasses={clsx(errors.password ? "border" : "border-none ", "bg-primary-350")}
                  placeholderStyle="text-black font-ssp font-medium text-base"
                  value={value}
                  onChange={(e) => {
                    const value = e.target.value;
                    handlePasswordChange(value);
                    onChange(e);
                  }}
                  onBlur={handlePasswordBlur}
                  error={errors.password}
                  // eslint-disable-next-line
                  errorMessage={getErrorMessage(
                    errors as Record<string, { type: any }>,
                    "password",
                    "Password"
                  )}
                  required
                  fullWidth
                />
              );
            }}
          />
          {showTooltip && (
            <Tooltip
              positionOfTip="bottom"
              bgColor="bg-secondary-800"
              tooltipStyles="!bottom-auto pb-4 md:pb-0 xl:mt-[22%] lg:mt-[22%] md:mt-[22%] sm:mt-[30%] xs:mt-[35%] mt-30% absolute"
            >
              <div className="z-20 px-5 py-4 rounded-md sm:w-[372px] w-fit">
                <div className="pb-[10px] text-black font-ssp font-bold text-sm">
                  Password criteria
                </div>
                <div className="flex items-center pb-2 text-sm font-normal font-ssp text-primary">
                  <span className="mr-1">
                    <DoubleVerifiedIcon />
                  </span>
                  Should be 8 words atleast
                </div>
                <div className="flex items-center pb-2 text-sm font-normal font-ssp text-error">
                  <span className="mr-1">
                    <DoubleVerifiedIcon fillColor="stroke-error" />
                  </span>
                  Should contain the special characters @$#+*
                </div>
                <div className="flex items-center pb-2 text-sm font-normal font-ssp text-grey-600">
                  <span className="mr-1">
                    <DoubleVerifiedIcon fillColor="stroke-grey-600" />
                  </span>
                  Should not be same as your email
                </div>
                <div className="flex items-center pb-2 text-sm font-normal font-ssp text-primary">
                  <span className="mr-1">
                    <DoubleVerifiedIcon />
                  </span>
                  Should contain atleast one uppercase letter
                </div>
                <div className="flex items-center pb-2 text-sm font-normal font-ssp text-error">
                  <span className="mr-1">
                    <DoubleVerifiedIcon fillColor="stroke-error" />
                  </span>
                  Should contain atleast one lowercase letter
                </div>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
      <div className={clsx(labelStyle, "md:pb-5 pb-3")}>Confirm password</div>
      <div className="w-screen sm:w-[400px] px-6 sm:px-0 md:pb-[99px] pb-6">
        <Controller
          control={control}
          name="confirmPassword"
          rules={{
            required: true,
            validate: {
              sameAs: () => {
                return getValues("password") === getValues("confirmPassword");
              },
            },
          }}
          render={({ field: { name, value, onChange } }) => {
            return (
              <TextField
                name={name}
                type="password"
                // placeholder="Confirm new password"
                inputClasses={clsx(
                  errors.confirmPassword ? "border" : "border-none ",
                  `bg-primary-350`
                )}
                placeholderStyle="text-black font-ssp font-medium text-base"
                value={value}
                onChange={(v) => onChange(v)}
                error={errors.confirmPassword}
                // eslint-disable-next-line
                errorMessage={getErrorMessage(
                  errors as Record<string, { type: any }>,
                  "confirmPassword",
                  "Confirm Password"
                )}
                required
                fullWidth
              />
            );
          }}
        />
      </div>
      <div className="flex justify-center w-full">
        <Button
          bgColor="bg-primary-1050"
          fontStyle={{
            fontFamily: "font-ssp",
            fontSize: "text-lg",
            fontWeight: "font-bold",
            fontColor: "text-white",
          }}
          borderStyle={{
            borderRadius: "rounded",
          }}
          className="w-[307px] h-[50px] sm:h-[60px]"
        >
          Confirm
        </Button>
      </div>
    </form>
  );
};

export default StepThree;
